import PropTypes from 'prop-types';
import { useState, useEffect, useMemo, useCallback, lazy } from 'react';
import { batch } from 'react-redux';

// material-ui
import { Box } from '@mui/material';
import { darken, lighten, styled } from '@mui/material/styles'; // useTheme
import { DataGridPro, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-license-pro';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';

// project imports
import { useSelector } from 'store';
import Loadable from 'ui-component/Loadable';
import SkeletonTableToolbar from 'ui-component/skeleton/TableToolbar';
import SkeletonTableFooter from 'ui-component/skeleton/TableFooter';
import RenderGridCell from 'ui-component/renders/RenderGridCell';

// project hooks
import useAuth from 'hooks/useAuth';

// assets
import ArrowDownwardTwoToneIcon from '@mui/icons-material/ArrowDownwardTwoTone';
import ArrowUpwardTwoToneIcon from '@mui/icons-material/ArrowUpwardTwoTone';

// lazy loaded components
const AppGridToolbar = Loadable(
    lazy(() => import('ui-component/custom/AppDataGrid/Components/Toolbar')),
    { loader: SkeletonTableToolbar }
);
const AppGridPaginator = Loadable(
    lazy(() => import('ui-component/custom/AppDataGrid/Components/Paginator')),
    { loader: SkeletonTableFooter }
);

LicenseInfo.setLicenseKey(process.env.REACT_APP_DATAGRIDPRO_LICENSE);

// ==============================|| APP DATA GRID ||============================== //

const getBackgroundColor = (color, mode) => (mode === 'dark' ? darken(color, 0.7) : lighten(color, 0.7));

const getHoverBackgroundColor = (color, mode) => (mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6));

const getSelectedBackgroundColor = (color, mode) => (mode === 'dark' ? darken(color, 0.5) : lighten(color, 0.5));

const getSelectedHoverBackgroundColor = (color, mode) => (mode === 'dark' ? darken(color, 0.4) : lighten(color, 0.4));

export const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    '& .MuiDataGrid-root': {
        border: 'none',
        '& .MuiDataGrid-cell': {
            borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
        },
        '& .MuiDataGrid-columnsContainer': {
            color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
            borderColor: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
        },
        '& .MuiDataGrid-columnSeparator': {
            color: theme.palette.mode === 'dark' ? theme.palette.text.primary + 15 : 'grey.200'
        }
    },
    '& .entity-status-deleted': {
        backgroundColor: getBackgroundColor(theme.palette.grey[500], theme.palette.mode),
        '&:hover': {
            backgroundColor: getHoverBackgroundColor(theme.palette.grey[500], theme.palette.mode)
        },
        '&.Mui-selected': {
            backgroundColor: getSelectedBackgroundColor(theme.palette.grey[500], theme.palette.mode),
            '&:hover': {
                backgroundColor: getSelectedHoverBackgroundColor(theme.palette.grey[500], theme.palette.mode)
            }
        }
    },
    '& .entity-status-canceled': {
        backgroundColor: getBackgroundColor(theme.palette.error.light, theme.palette.mode),
        '&:hover': {
            backgroundColor: getHoverBackgroundColor(theme.palette.error.light, theme.palette.mode)
        },
        '&.Mui-selected': {
            backgroundColor: getSelectedBackgroundColor(theme.palette.error.light, theme.palette.mode),
            '&:hover': {
                backgroundColor: getSelectedHoverBackgroundColor(theme.palette.error.light, theme.palette.mode)
            }
        }
    }
}));

export const AppDataGridLocales = {
    noRowsLabel: <FormattedMessage id="ui-comp-table-no-rows" />,
    noResultsOverlayLabel: <FormattedMessage id="ui-comp-table-no-results" />,
    errorOverlayDefaultLabel: <FormattedMessage id="ui-comp-snackbar-genericerror-text" />,
    toolbarDensity: <FormattedMessage id="ui-comp-button-density" />,
    toolbarDensityCompact: <FormattedMessage id="ui-comp-table-density-compact" />,
    toolbarDensityStandard: <FormattedMessage id="ui-comp-table-density-standard" />,
    toolbarDensityComfortable: <FormattedMessage id="ui-comp-table-density-comfortable" />,
    toolbarColumns: <FormattedMessage id="ui-comp-button-settings" />,
    toolbarFilters: <FormattedMessage id="ui-comp-button-filter" />,
    toolbarFiltersTooltipHide: false,
    toolbarFiltersTooltipShow: false,
    columnsPanelTextFieldLabel: <FormattedMessage id="ui-comp-button-columns-findcolumn" />,
    columnsPanelTextFieldPlaceholder: '',
    columnMenuHideColumn: <FormattedMessage id="ui-comp-button-columns-hide" />,
    columnsPanelShowAllButton: <FormattedMessage id="ui-comp-button-columns-showall" />,
    columnsPanelHideAllButton: <FormattedMessage id="ui-comp-button-columns-hideall" />,
    columnMenuManageColumns: <FormattedMessage id="ui-comp-button-columns-manage" />,
    columnMenuSortAsc: <FormattedMessage id="ui-comp-button-columns-sortasc" />,
    columnMenuSortDesc: <FormattedMessage id="ui-comp-button-columns-sortdesc" />,
    columnMenuUnsort: <FormattedMessage id="ui-comp-button-columns-unsort" />,
    pinToLeft: <FormattedMessage id="ui-comp-button-columns-pintoleft" />,
    pinToRight: <FormattedMessage id="ui-comp-button-columns-pintoright" />,
    unpin: <FormattedMessage id="ui-comp-button-columns-unpin" />,
    toolbarExport: <FormattedMessage id="ui-comp-button-export" />,
    columnHeaderSortIconLabel: <FormattedMessage id="ui-comp-button-sort" />,
    footerRowSelected: (count) => <FormattedMessage id="ui-comp-table-elements-selected" values={{ countSelected: count?.toString() }} />
};

const AppDataGrid = ({
    entityName = '',
    entityPreffix,
    entitySuffix,
    entityLabels = {},
    permissionData = [],
    selectionActions = {},
    filterActions = {},
    quickSearchActions = {},
    sortingActions = {},
    paginationActions = {},
    exportActions = {},
    importActions = {},
    onClickActions = {},
    hideToolbar = false,
    apiRef: externalApiRef,
    rowReordering = null,
    ...others
}) => {
    // const theme = useTheme();
    const intl = useIntl();
    const internalApiRef = useGridApiRef();
    const apiRef = externalApiRef || internalApiRef;

    const { profileSettings } = useAuth();
    const { colDef, sortingDef, list, isLoading, limit, selectedRows, dataGridState } = useSelector((state) => state[entityName] || {});

    const [gridDensity, setGridDensity] = useState('compact');

    const buildRowReorderingActions = useCallback(
        (row) => {
            if (!rowReordering?.enabled) {
                return [];
            }

            const requiredPermission = rowReordering?.requiredPermission;
            if (requiredPermission && !permissionData?.includes(requiredPermission)) {
                return [];
            }

            const reorderState = typeof rowReordering?.getRowReorderState === 'function' ? rowReordering.getRowReorderState(row) || {} : {};
            const actions = [];

            if (typeof rowReordering?.onMoveUp === 'function' && reorderState?.canMoveUp !== false) {
                actions.push({
                    key: `actionmoveup-row${row?.id}`,
                    icon: <ArrowUpwardTwoToneIcon fontSize="small" />,
                    label: 'ui-comp-button-moveup',
                    position: -2,
                    showInMenu: true,
                    disabled: rowReordering?.disabled === true || row?.deletedAt,
                    onClick: () => rowReordering.onMoveUp(row)
                });
            }

            if (typeof rowReordering?.onMoveDown === 'function' && reorderState?.canMoveDown !== false) {
                actions.push({
                    key: `actionmovedown-row${row?.id}`,
                    icon: <ArrowDownwardTwoToneIcon fontSize="small" />,
                    label: 'ui-comp-button-movedown',
                    position: -1,
                    showInMenu: true,
                    disabled: rowReordering?.disabled === true || row?.deletedAt,
                    onClick: () => rowReordering.onMoveDown(row)
                });
            }

            return actions;
        },
        [permissionData, rowReordering]
    );

    const columns = useMemo(
        () =>
            colDef?.map((col) => {
                if (col?.field === '__detail_panel_toggle__') {
                    return col;
                }

                const newCol = { ...col, headerName: intl.formatMessage({ id: col?.label }) };

                if (col?.type === 'actions') {
                    Object.assign(newCol, {
                        getActions: (params) =>
                            [...buildRowReorderingActions(params?.row), ...(col?.getActions(params, permissionData, onClickActions) || [])]
                                ?.sort((a1, a2) => (a1?.position > a2?.position ? 1 : -1))
                                ?.map((action) => <GridActionsCellItem {...action} label={intl.formatMessage({ id: action?.label })} />)
                    });
                } else if ('renderCell' in (col || {}) && typeof col.renderCell === 'function') {
                    Object.assign(newCol, {
                        renderCell: (params) => col.renderCell(params, permissionData, onClickActions)
                    });
                } else if (!('renderCell' in (col || {})) && !('valueFormatter' in (col || {})) && !('valueGetter' in (col || {}))) {
                    Object.assign(newCol, { renderCell: (params) => <RenderGridCell {...params} /> });
                }

                return newCol;
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [buildRowReorderingActions, colDef, permissionData, onClickActions]
    );

    const onCellDoubleClick = useCallback(
        (params) => {
            if (
                !params?.row?.deletedAt &&
                permissionData?.includes('canEdit') &&
                'onClickRowActionEdit' in (onClickActions || {}) &&
                typeof onClickActions.onClickRowActionEdit === 'function'
            ) {
                onClickActions.onClickRowActionEdit(params?.row);
            } else if (
                permissionData?.includes('canView') &&
                'onClickRowActionView' in (onClickActions || {}) &&
                typeof onClickActions.onClickRowActionView === 'function'
            ) {
                onClickActions.onClickRowActionView(params?.row);
            }
        },
        [permissionData, onClickActions]
    );
    const onRowSelectionModelChange = useCallback(
        (model) => {
            if (typeof selectionActions?.handleSelectRows === 'function') {
                selectionActions.handleSelectRows(model);
            }
        },
        [selectionActions]
    );
    const onSortModelChange = useCallback(
        (model) =>
            batch(() => {
                if (model?.length > 0) {
                    const newModel = [];
                    model.forEach((element) => {
                        const field = sortingDef.find((x) => x.fieldName === element.field);
                        if (field) {
                            newModel.push({ field, direction: element.sort });
                        }
                    });

                    if (newModel.length > 0 && typeof sortingActions?.handleApplySorting === 'function') {
                        sortingActions.handleApplySorting(newModel);
                    }
                } else if (typeof sortingActions?.handleResetSorting === 'function') {
                    sortingActions.handleResetSorting();
                }
            }),
        [sortingDef, sortingActions]
    );
    const getRowClassName = useCallback((params) => {
        if (params?.row?.state === 'canceled') {
            return 'entity-status-canceled';
        }
        if (params?.row?.deletedAt) {
            return 'entity-status-deleted';
        }

        return '';
    }, []);

    useEffect(() => {
        const settingNameStruc = `CustomSettings-${entityPreffix ? `${entityPreffix}-` : ''}${entityName}${
            entitySuffix ? `-${entitySuffix}` : ''
        }`;
        const customProfSetts =
            Array.isArray(profileSettings) && profileSettings?.length > 0
                ? profileSettings?.find((sett) => sett?.name === settingNameStruc)
                : null;

        if (customProfSetts && 'fieldValue' in (customProfSetts || {})) {
            const { columns, sorting, toolbarFilters } = customProfSetts?.fieldValue;

            if (columns || sorting) {
                apiRef.current.restoreState({ columns, sorting });
            }
            if (
                toolbarFilters &&
                'onClickToolbarActionToogleFilter' in (onClickActions || {}) &&
                typeof onClickActions.onClickToolbarActionToogleFilter === 'function'
            ) {
                Object.keys(toolbarFilters)?.map((filterName) =>
                    onClickActions.onClickToolbarActionToogleFilter(filterName, toolbarFilters[filterName])
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ height: 'calc(100vh - 260px)', width: '100%' }}>
            <StyledDataGrid
                apiRef={apiRef}
                rows={list || []}
                columns={columns || []}
                pageSize={limit}
                loading={isLoading}
                density={gridDensity}
                pagination
                disableColumnFilter
                initialState={dataGridState}
                localeText={AppDataGridLocales}
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={onRowSelectionModelChange}
                onCellDoubleClick={onCellDoubleClick}
                onSortModelChange={onSortModelChange}
                getRowClassName={getRowClassName}
                slots={{
                    ...(hideToolbar ? {} : { toolbar: AppGridToolbar }),
                    pagination: AppGridPaginator
                }}
                slotProps={{
                    ...(hideToolbar
                        ? {}
                        : {
                              toolbar: {
                                  entityName,
                                  entityPreffix,
                                  entitySuffix,
                                  entityLabels,
                                  permissionData,
                                  filterActions,
                                  quickSearchActions,
                                  sortingActions,
                                  exportActions,
                                  importActions,
                                  onClickActions: {
                                      ...onClickActions,
                                      onClickToolbarActionSetDensityGrid: (newGridDensity) => setGridDensity(newGridDensity)
                                  }
                              }
                          }),
                    pagination: {
                        entityName,
                        paginationActions
                    }
                }}
                {...others}
            />
        </Box>
    );
};

AppDataGrid.propTypes = {
    entityName: PropTypes.string.isRequired,
    entityPreffix: PropTypes.string,
    entitySuffix: PropTypes.string,
    entityLabels: PropTypes.object,
    permissionData: PropTypes.arrayOf(PropTypes.string),
    selectionActions: PropTypes.object,
    filterActions: PropTypes.object,
    quickSearchActions: PropTypes.object,
    sortingActions: PropTypes.object,
    paginationActions: PropTypes.object,
    exportActions: PropTypes.object,
    importActions: PropTypes.object,
    onClickActions: PropTypes.object,
    hideToolbar: PropTypes.bool,
    apiRef: PropTypes.object,
    rowReordering: PropTypes.object
};

export default AppDataGrid;
